import classNames from 'classnames';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export interface OptionContentProps {
  // `icon` is rendered as <BenchlingIcon icon={icon} /> in the option.
  icon?: BenchlingIconName;
  iconClassName?: string;
  iconWrapperClassName?: string;
  avatar?: React.ReactNode;
  avatarClassName?: string;
  contentClassName?: string;
  childrenClassName?: string;
  optionNameClassName?: string;
  optionMetadataClassName?: string;
  optionChevronClassName?: string;
  // Rendered under the main text.
  metadata?: React.ReactNode;
  // The content of the option.
  children: React.ReactNode;
  hasSubmenu?: boolean;
  className?: string;
  /** Must be string or element; matches ui-library Tooltip `text` (not arbitrary React nodes like numbers). */
  tooltipTitle?: JSX.Element | string | null;
  tooltipPlacement?: FloatingPlacement;
  dataTestAttrs?: DataTestAttrs;
  /**
   * If supplied, `titleAttr` populates the `title` attribute of the `menu-option-children` div tag. The
   * `title` attribute is rendered as a tooltip by the browser. This is separate from the tooltip configured
   * by the `tooltipTitle` prop. The motivation for this tooltip is to reveal the full text of the menu
   * item when css ellipsization is enabled, which happens in Option / OptionContainer. In this case
   * `titleAttr` should have the same value as `children`, if `children` is a string.
   */
  titleAttr?: string;
}

/**
 * OptionContent is the presentational component rendered in Option. It arranges the icon, text, and metadata.
 */
function OptionContent(props: OptionContentProps): JSX.Element {
  return (
    <Tooltip text={props.tooltipTitle} placement={props.tooltipPlacement} disableContentSelection={true}>
      <div
        className={classNames('menu-optionContent', props.className)}
        {...getTestProps(props.dataTestAttrs)}
      >
        {props.icon && (
          <div
            className={classNames('menu-option-iconWrapper', props.iconWrapperClassName)}
            aria-hidden="true"
          >
            <BenchlingIcon
              className={classNames('menu-option-icon', props.iconClassName)}
              icon={props.icon}
            />
          </div>
        )}
        {props.avatar && <div className={props.avatarClassName}>{props.avatar}</div>}
        <div
          className={classNames('menu-option-children', props.childrenClassName)}
          title={props.titleAttr}
          {...getTestProps({component: 'OptionContent', element: 'optionNameAndMetadata'})}
        >
          <div
            className={classNames('menu-option-name', props.optionNameClassName)}
            role="presentation"
            {...getTestProps({component: 'OptionContent', element: 'optionName'})}
          >
            {props.children}
          </div>
          <div
            className={classNames('menu-option-metadata', props.optionMetadataClassName)}
            {...getTestProps({component: 'OptionContent', element: 'optionMetadata'})}
          >
            {props.metadata}
          </div>
        </div>
        {props.hasSubmenu && (
          <div className="menu-option-chevronWrapper" aria-hidden="true">
            <BenchlingIcon
              className={classNames('menu-option-chevron', props.optionChevronClassName)}
              icon="chevron"
            />
          </div>
        )}
      </div>
    </Tooltip>
  );
}

export default OptionContent;
